import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { CASE_REGISTRY, deepClone } from "@/data/contentRegistry";
import { SECTIONS, SIM_BY_ID } from "@/data/simulators";
import { ContentEditor } from "@/components/admin/JsonNodeEditor";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw, FlaskConical, Pencil, Eye, EyeOff, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function AdminSimulators() {
  const { refresh } = useContent();
  const [selected, setSelected] = useState(CASE_REGISTRY[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overridden, setOverridden] = useState({});
  const [confirmReset, setConfirmReset] = useState(false);
  const [vis, setVis] = useState({});
  const [free, setFree] = useState({});
  const [eta, setEta] = useState({});
  const [visBusy, setVisBusy] = useState(null);
  const [registry, setRegistry] = useState(null);

  const reg = useMemo(() => CASE_REGISTRY.find((c) => c.key === selected), [selected]);

  const loadVis = () => {
    api.get("/api/admin/content/visibility/simulators")
      .then(({ data }) => setVis(data.data && typeof data.data === "object" ? data.data : {}))
      .catch(() => {});
  };

  const loadFree = () => {
    api.get("/api/admin/content/visibility/free_simulators")
      .then(({ data }) => setFree(data.data && typeof data.data === "object" ? data.data : {}))
      .catch(() => {});
  };

  const loadEta = () => {
    api.get("/api/admin/content/visibility/eta_simulators")
      .then(({ data }) => setEta(data.data && typeof data.data === "object" ? data.data : {}))
      .catch(() => {});
  };

  const loadRegistry = () => {
    api.get("/api/admin/simulators/registry")
      .then(({ data }) => setRegistry(data))
      .catch(() => {});
  };

  const isVisible = (id) => vis[id] !== false;
  const isFree = (id) => free[id] === true;

  const toggleVis = async (id, visible) => {
    const prev = vis;
    const next = { ...vis, [id]: visible };
    setVis(next);
    setVisBusy(id);
    try {
      await api.put("/api/admin/content/visibility/simulators", { data: next });
      toast.success(`${SIM_BY_ID[id].title} ${visible ? "shown to" : "hidden from"} trainees.`);
      refresh();
    } catch (e) {
      setVis(prev);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setVisBusy(null); }
  };

  const toggleFree = async (id, isFreeTier) => {
    const prev = free;
    const next = { ...free, [id]: isFreeTier };
    setFree(next);
    setVisBusy(id);
    try {
      await api.put("/api/admin/content/visibility/free_simulators", { data: next });
      toast.success(`${SIM_BY_ID[id].title} ${isFreeTier ? "is now free-tier" : "is now Pro-only"}.`);
      refresh();
    } catch (e) {
      setFree(prev);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setVisBusy(null); }
  };

  const setSimEta = async (id, value) => {
    const prev = eta;
    const next = { ...eta };
    if (value) next[id] = value; else delete next[id];
    setEta(next);
    try {
      await api.put("/api/admin/content/visibility/eta_simulators", { data: next });
      refresh();
    } catch (e) {
      setEta(prev);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const loadMeta = () => {
    api.get("/api/admin/content").then(({ data }) => {
      const map = {};
      (data.overrides || []).forEach((o) => { if (o.kind === "cases") map[o.key] = o.updated_at; });
      setOverridden(map);
    }).catch(() => {});
  };

  const loadDoc = (key) => {
    setLoading(true);
    api.get(`/api/admin/content/cases/${key}`).then(({ data }) => {
      const reg = CASE_REGISTRY.find((c) => c.key === key);
      setData(data.data != null ? data.data : deepClone(reg.defaults));
    }).catch(() => {
      const reg = CASE_REGISTRY.find((c) => c.key === key);
      setData(deepClone(reg.defaults));
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { loadVis(); }, []);
  useEffect(() => { loadFree(); }, []);
  useEffect(() => { loadEta(); }, []);
  useEffect(() => { loadRegistry(); }, []);
  useEffect(() => { loadDoc(selected); }, [selected]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/admin/content/cases/${selected}`, { data });
      toast.success(`Saved ${reg.label}. Changes are live for all trainees.`);
      loadMeta();
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const reset = async () => {
    setConfirmReset(false);
    try {
      await api.delete(`/api/admin/content/cases/${selected}`);
      toast.success(`${reg.label} reset to the built-in default.`);
      setData(deepClone(reg.defaults));
      loadMeta();
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-simulators-page">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#5ec0ff] font-semibold mb-1">
        <FlaskConical className="w-3.5 h-3.5" /> Simulator content
      </div>
      <h1 className="text-2xl font-bold text-white">Cases & responses</h1>
      <p className="text-sm text-slate-400 mt-1">Edit each simulator's cases and answer options. Saving publishes instantly to all trainees.</p>

      {/* ---- data integrity: registered simulators + orphan scan ---- */}
      {registry && (
        <div className="mt-6 rounded-xl border border-slate-800 bg-[#0e1726] p-5" data-testid="admin-sim-registry">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#5ec0ff] font-semibold">
            <ShieldCheck className="w-3.5 h-3.5" /> Data integrity
          </div>
          <h2 className="text-lg font-bold text-white mt-1">Registered simulators</h2>
          <p className="text-sm text-slate-400 mt-1">
            <span className="text-white font-semibold" data-testid="registry-count">{registry.total_registered}</span> simulators are
            registered on the server. Saved scores are keyed by simulator&nbsp;ID — keep IDs stable, and register any new
            simulator in <code className="text-slate-300 bg-black/30 rounded px-1">backend/core/simulators.py</code>.
          </p>

          {registry.orphans?.length > 0 ? (
            <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3" data-testid="admin-sim-orphans">
              <div className="flex items-center gap-2 text-amber-300 font-semibold text-sm">
                <AlertTriangle className="w-4 h-4" />
                {registry.orphans.length} unregistered simulator ID{registry.orphans.length > 1 ? "s" : ""} found in saved records
              </div>
              <p className="text-xs text-amber-200/80 mt-1">
                These IDs appear in learners' saved score records but aren't in the current registry — usually a renamed/removed
                simulator or a typo. Existing records still work; new saves for these IDs are flagged and logged.
              </p>
              <ul className="mt-2 space-y-1">
                {registry.orphans.map((o) => (
                  <li key={o.simulator_id}
                      className="flex items-center justify-between text-xs text-amber-100 bg-black/20 rounded px-2 py-1"
                      data-testid={`orphan-${o.simulator_id}`}>
                    <span className="font-mono">{o.simulator_id}</span>
                    <span className="text-amber-300/80">{o.sessions} record{o.sessions > 1 ? "s" : ""}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="mt-3 flex items-center gap-2 text-sm text-emerald-400" data-testid="admin-sim-registry-ok">
              <CheckCircle2 className="w-4 h-4" /> All saved records match a registered simulator.
            </div>
          )}
        </div>
      )}

      {/* ---- simulator visibility ---- */}
      <div className="mt-6 rounded-xl border border-slate-800 bg-[#0e1726] p-5" data-testid="admin-sim-visibility">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#5ec0ff] font-semibold">
          <Eye className="w-3.5 h-3.5" /> Visibility
        </div>
        <h2 className="text-lg font-bold text-white mt-1">Show or hide simulators</h2>
        <p className="text-sm text-slate-400 mt-1">
          The green toggle shows/hides a simulator for everyone. The amber <span className="text-amber-400 font-semibold">Free</span> toggle
          makes it available on the free tier (otherwise it's Pro-only). If every simulator in a section is hidden,
          that section shows <span className="text-amber-400 font-semibold">Coming soon</span>. When a
          simulator is hidden you can set an optional <span className="text-amber-400 font-semibold">release month</span>,
          which shows as its date (e.g. "Aug 2026") on the public Simulators page — otherwise it reads "TBA".
        </p>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {SECTIONS.map((sec) => {
            const visibleCount = sec.sims.filter(isVisible).length;
            return (
              <div key={sec.id} className="rounded-lg border border-slate-800 bg-[#0b1320] p-3" data-testid={`admin-vis-section-${sec.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-white">{String(sec.n).padStart(2, "0")} · {sec.title}</div>
                  {visibleCount === 0 && (
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30" data-testid={`admin-vis-coming-soon-${sec.id}`}>
                      Coming soon
                    </span>
                  )}
                </div>
                <div className="space-y-1.5">
                  {sec.sims.map((id) => {
                    const sim = SIM_BY_ID[id];
                    const on = isVisible(id);
                    return (
                      <div key={id} className="flex items-center justify-between gap-3 px-2.5 py-2 rounded-md hover:bg-slate-800/60 transition-colors">
                        <div className="flex items-center gap-2 min-w-0">
                          {on ? <Eye className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> : <EyeOff className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
                          <span className={`text-sm truncate ${on ? "text-slate-200" : "text-slate-500"}`}>{sim.title}</span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {!on && (
                            <input
                              type="month"
                              value={eta[id] || ""}
                              onChange={(e) => setSimEta(id, e.target.value)}
                              data-testid={`admin-eta-${id}`}
                              title="Expected release month (shown as the 'Coming soon' date)"
                              className="bg-[#0b1320] border border-amber-500/40 rounded px-1.5 py-1 text-[11px] text-amber-300 w-[132px] [color-scheme:dark]"
                            />
                          )}
                          <label className="flex items-center gap-1.5 cursor-pointer" title="Available on the free tier">
                            <span className={`text-[9px] font-bold uppercase tracking-wider ${isFree(id) && on ? "text-amber-400" : "text-slate-600"}`}>Free</span>
                            <Switch
                              checked={isFree(id)}
                              disabled={visBusy === id || !on}
                              onCheckedChange={(v) => toggleFree(id, v)}
                              data-testid={`admin-free-toggle-${id}`}
                              className="data-[state=checked]:bg-amber-500 scale-90"
                            />
                          </label>
                          <span className="w-px h-5 bg-slate-700" />
                          <Switch
                            checked={on}
                            disabled={visBusy === id}
                            onCheckedChange={(v) => toggleVis(id, v)}
                            data-testid={`admin-vis-toggle-${id}`}
                            className="data-[state=checked]:bg-emerald-500"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-4 gap-5">
        {/* simulator list */}
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-slate-800 bg-[#0e1726] p-2 space-y-1" data-testid="admin-sim-list">
            {CASE_REGISTRY.map((c) => (
              <button
                key={c.key}
                onClick={() => setSelected(c.key)}
                data-testid={`admin-sim-${c.key}`}
                className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${selected === c.key ? "bg-[#0047AB] text-white" : "text-slate-300 hover:bg-slate-800"}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium truncate">{c.label}</span>
                  {overridden[c.key] && <Pencil className="w-3 h-3 text-amber-400 shrink-0" title="Edited" />}
                </div>
                <div className={`text-[10px] ${selected === c.key ? "text-blue-100" : "text-slate-500"}`}>{c.section}</div>
              </button>
            ))}
          </div>
        </div>

        {/* editor */}
        <div className="lg:col-span-3">
          <div className="rounded-xl border border-slate-800 bg-white">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 flex-wrap">
              <div>
                <div className="text-sm font-semibold text-slate-900">{reg?.label}</div>
                <div className="text-[11px] text-slate-500">
                  {Array.isArray(data) ? `${data.length} case${data.length === 1 ? "" : "s"}` : ""}
                  {overridden[selected] ? " · customised" : " · default"}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={!overridden[selected]} onClick={() => setConfirmReset(true)} data-testid="admin-sim-reset" className="text-slate-600 text-xs">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset to default
                </Button>
                <Button size="sm" disabled={saving || loading} onClick={save} data-testid="admin-sim-save" className="bg-[#0047AB] hover:bg-[#003380] text-white text-xs">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save changes
                </Button>
              </div>
            </div>
            <div className="p-4 max-h-[70vh] overflow-y-auto">
              {loading || data == null ? (
                <div className="flex items-center justify-center gap-2 text-slate-400 text-sm py-16">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading content…
                </div>
              ) : (
                <ContentEditor data={data} onChange={setData} />
              )}
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent data-testid="admin-sim-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to built-in default?</AlertDialogTitle>
            <AlertDialogDescription>
              This discards all custom edits for <strong>{reg?.label}</strong> and restores the original content shipped with the app.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-sim-reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={reset} data-testid="admin-sim-reset-confirm" className="bg-rose-600 hover:bg-rose-700">Reset</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
